import { api } from "@/services/api";

export type DidDestinationScope = "global" | "restricted";

export interface DidDestination {
  scope: DidDestinationScope;
  project_ids?: string[];
}

export interface DidDestinationProject {
  id: string;
  name: string;
}

export interface AvailableDid {
  id: string;
  did: string;
  description?: string;
  status?: string;
  provider?: "manual" | "aitrunk";
  external_sync_status?: string | null;
  next_billing_at?: string | null;
  billing_project_id?: string | null;
  last_charged_at?: string | null;
  last_charge_amount?: number | null;
  last_charge_currency?: string | null;
  locked_price_local?: number | null;
  locked_currency?: string | null;
  cache_sync_status?: string | null;
  in_use: boolean;
  used_by_agent_id?: string;
  used_by_agent_name?: string;
  destination_scope?: DidDestinationScope;
  destination_projects?: DidDestinationProject[];
}

export interface ProviderDidPool {
  ddd?: string;
  dids: Array<{
    number: string;
    ddd: string;
    city?: string;
    provider: string;
  }>;
}

export interface ProviderDidQuote {
  service_name: "telephony_did";
  pricing_rule_name: "monthly";
  base_amount: number;
  base_currency: "USD";
  amount: number;
  currency: string;
  exchange_rate: number;
  tax_rate: number;
  billing_period: "month";
}

export interface AllocatedDid extends AvailableDid {
  provider: "aitrunk";
  allocated_at: string;
}

export interface ReleasedDid {
  id: string;
  status: "released";
  released_at: string;
}

export interface AllocateProviderDidParams {
  ddd?: string;
  number?: string;
  agentId?: string;
  destination?: DidDestination;
}

export const organizationDidApi = {
  /**
   * Numbers available to link to an agent. When `projectId` is provided the
   * backend narrows the list to numbers usable by that project; without it the
   * org-wide list is returned (kept for backward compatibility).
   */
  getAvailableDids: async (projectId?: string) => {
    const { data } = await api.get<AvailableDid[]>("/organization/available-dids", {
      params: projectId ? { project_id: projectId } : undefined,
    });
    return data;
  },
  getOwnedDids: async () => {
    const { data } = await api.get<AvailableDid[]>("/organization/dids");
    return data;
  },
  getProviderPool: async (ddd?: string) => {
    const { data } = await api.get<ProviderDidPool>(
      "/organization/dids/provider/available",
      { params: ddd ? { ddd } : undefined },
    );
    return data;
  },
  getProviderQuote: async () => {
    const { data } = await api.get<ProviderDidQuote>("/organization/dids/provider/quote");
    return data;
  },
  /**
   * Allocate a number from the provider. `agentId` is optional — when omitted
   * the number is purchased without being linked to any agent (organization
   * inventory flow). `destination` scopes which projects may use the number.
   * Undefined keys are dropped by the JSON body serializer, so the backend
   * never receives an explicit `agent_id: null`.
   */
  allocateProviderDid: async (
    params: AllocateProviderDidParams,
    idempotencyKey: string,
  ) => {
    const { ddd, number, agentId, destination } = params;
    const { data } = await api.post<AllocatedDid>(
      "/organization/dids/provider/allocate",
      { ddd, number, agent_id: agentId, destination },
      { headers: { "Idempotency-Key": idempotencyKey } },
    );
    return data;
  },
  updateDidDestination: async (didId: string, body: DidDestination) => {
    const { data } = await api.patch<AvailableDid>(
      `/organization/dids/${didId}/destination`,
      body,
    );
    return data;
  },
  unassignDid: async (didId: string) => {
    const { data } = await api.post<AvailableDid>(
      `/organization/dids/${didId}/unassign`,
    );
    return data;
  },
  /**
   * Link a DID to an agent. Directs the number's calls to the agent and
   * automatically enables inbound calling for it. 204 on success; 400 when
   * the DID isn't active, 409 when it's already in use by another agent or
   * the agent's project falls outside the DID's destination.
   */
  assignDid: async (didId: string, agentId: string) => {
    await api.post<void>(`/organization/dids/${didId}/assign`, { agent_id: agentId });
  },
  releaseProviderDid: async (didId: string) => {
    const { data } = await api.post<ReleasedDid>(
      `/organization/dids/${didId}/release`,
    );
    return data;
  },
};
